import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const TreatmentCarousel = () => {
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Pagination settings
  const itemsPerPage = 6; // 2 rows × 3 columns
  const totalPages = Math.ceil(treatments.length / itemsPerPage);

  useEffect(() => {
    fetchTreatments();
  }, []);

  const fetchTreatments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use direct API call like other components in the project
      const response = await fetch('https://medical-linkers.onrender.com/api/treatments?status=published&limit=50');
      const data = await response.json();
      
      if (data.treatments) {
        setTreatments(data.treatments);
      } else {
        setTreatments([]);
      }
    } catch (error) {
      console.error('Error fetching treatments:', error);
      setError('Failed to load treatments');
      setTreatments([]);
    } finally {
      setLoading(false);
    }
  };

  const getMinimumCost = (treatment) => {
    if (!treatment.costInfo) return 'USD $5,500';
    
    // Find the minimum cost from all countries
    const costs = [];
    Object.values(treatment.costInfo).forEach(countryInfo => {
      if (countryInfo.minCost) {
        // Extract numeric value from cost string (e.g., "USD $5,500" -> 5500)
        const match = countryInfo.minCost.match(/[\d,]+/);
        if (match) {
          const numericCost = parseInt(match[0].replace(/,/g, ''));
          costs.push(numericCost);
        }
      }
    });
    
    if (costs.length > 0) {
      const minCost = Math.min(...costs);
      return `USD $${minCost.toLocaleString()}`;
    }
    
    return 'USD $5,500'; // Default fallback
  };

  const getCurrentPageTreatments = () => {
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return treatments.slice(startIndex, endIndex);
  };

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  if (loading) {
    return (
      <section className="bg-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-4xl sm:text-5xl font-bold text-black">
            Start Your <span className="text-blue-600">Treatment Journey</span>{" "}
            <span className="text-black">Now</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-600 mt-2">
            Loading treatments...
          </p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="bg-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-4xl sm:text-5xl font-bold text-black">
            Start Your <span className="text-blue-600">Treatment Journey</span>{" "}
            <span className="text-black">Now</span>
          </h2>
          <p className="text-base sm:text-lg text-red-600 mt-2">{error}</p>
        </div>
      </section>
    );
  }

  const currentTreatments = getCurrentPageTreatments();

  return (
    <section className="bg-white py-16 px-4 sm:px-6 lg:px-8 relative min-h-[600px] flex flex-col">
      {/* Heading */}
      <div className="text-center mb-10">
        <h2 className="text-5xl sm:text-6xl font-bold text-black">
          Start Your <span className="text-blue-600">Treatment Journey</span>{" "}
          <span className="text-black">Now</span>
        </h2>
        <p className="text-lg sm:text-xl text-gray-600 mt-2">
          Choose from {treatments.length}+ globally 5-star rated treatments
        </p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-2 gap-y-6 place-items-center flex-grow">
        {currentTreatments.map((treatment) => (
          <div
            key={treatment.id}
            className="border-2 border-blue-400 rounded-xl p-4 w-full max-w-[320px] text-left shadow hover:shadow-lg transition duration-300"
          >
            <h3 className="text-2xl font-semibold text-blue-600 mb-2">
              {treatment.title}
            </h3>
            <p className="text-lg text-gray-800 mb-1">Starts from</p>
            <p className="text-xl font-medium text-black mb-2">
              {getMinimumCost(treatment)}
            </p>
            <div className="flex items-center text-yellow-400 text-lg mb-1">
              {[...Array(5)].map((_, i) => (
                <svg
                  key={i}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  className="w-6 h-6"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.004 3.092a1 1 0 00.95.69h3.25c.969 0 1.371 1.24.588 1.81l-2.63 1.91a1 1 0 00-.364 1.118l1.004 3.092c.3.921-.755 1.688-1.54 1.118l-2.63-1.91a1 1 0 00-1.175 0l-2.63 1.91c-.784.57-1.838-.197-1.539-1.118l1.004-3.092a1 1 0 00-.364-1.118l-2.63-1.91c-.784-.57-.38-1.81.588-1.81h3.25a1 1 0 00.951-.69l1.004-3.092z" />
                </svg>
              ))}
            </div>
            <p className="text-base text-gray-600 mb-4">
              Expert Doctors | Top Hospitals
            </p>
            <button className="text-lg font-semibold text-black underline hover:text-blue-600 transition">
              <Link to={`/treatments/${treatment.slug}`}>
                Enquire More
              </Link>
            </button>
          </div>
        ))}
      </div>

      {/* Pagination - Only show if there are multiple pages */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8">
          <div className="flex justify-center gap-4 bg-white rounded-full shadow-lg border border-gray-200 px-4 py-2">
            <button 
              onClick={prevPage}
              className="w-10 h-10 flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-colors"
              disabled={totalPages <= 1}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            
            {/* Page indicators */}
            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPage(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentPage ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            
            <button 
              onClick={nextPage}
              className="w-10 h-10 flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-colors"
              disabled={totalPages <= 1}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default TreatmentCarousel;
